// src/scheduling/components/appointments/inline-edit-appointment-panel.tsx
import { useEffect, useRef, type ComponentProps } from 'react';
import { toast } from 'sonner';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import { useAppointmentDetails } from '@/services/scheduling/use-appointment-details';
import { appointmentToFormValues } from '@/scheduling/lib/appointment-to-form-values';
import { useAuthStore } from '@/auth/auth-store';
import { isDeviceAppointment } from '@/scheduling/lib/device-edit-request';
import { BookDevicePanel } from '@/scheduling/components/devices/book-device-panel';
import type { OrgLocation } from '@/auth/auth-api';
import type { CalendarDevice } from '@/types/scheduling-api';
import { CreateAppointmentPanel } from './create-appointment-panel';

interface Props {
  organizationId: string;
  practiceId: string;
  userId: string;
  onSearchPatients: ComponentProps<typeof CreateAppointmentPanel>['onSearchPatients'];
  /** Geometry pass-through — the frame resolves the per-org layout gate
   *  once and hands the same edge/layout to all three rails so they can
   *  never disagree (see use-booking-rail-layout.ts). */
  edge?: ComponentProps<typeof CreateAppointmentPanel>['edge'];
  layout?: ComponentProps<typeof CreateAppointmentPanel>['layout'];
  /**
   * Called with the appointment's NEW start after a successful save so the
   * calendar can follow a reschedule onto another day (it used to stay on the
   * old day, hiding the appointment the user just moved).
   */
  onEditSaveSuccess?: (newStart: string | null) => void;
  /** Calendar's selected day as `YYYY-MM-DD` — see CreateAppointmentPanel. */
  calendarDateYmd?: string;
  /**
   * PROD-2526 — what the Book-a-device rail needs when the opened appointment
   * is a DEVICE booking: the device list its (fixed) device card is drawn from,
   * and the practice locations. Ignored for a practitioner appointment.
   */
  devices?: CalendarDevice[];
  locations?: OrgLocation[];
}

/**
 * Inline edit/reschedule rail — Flutter parity for the appointment popup's
 * pencil / Reschedule (`calendar_tab.dart:6419-6445`), which open the editor
 * IN PLACE on the calendar rather than navigating to the full appointment
 * screen (that's the 3-dots path → ViewAppointmentPanelPage).
 *
 * Reads `editTarget` (ids + mode) from the scheduling store, fetches the
 * appointment via React Query (`useAppointmentDetails`, the same PreLoad the
 * popup uses so it's warm in cache), and mounts `CreateAppointmentPanel` in
 * edit/reschedule mode. Renders nothing until a target is set and the
 * appointment has loaded, so the rail only appears when actually editing
 * (side and width follow the edge/layout pass-through from the frame).
 */
export function InlineEditAppointmentPanel({
  organizationId,
  practiceId,
  userId,
  onSearchPatients,
  edge,
  layout,
  onEditSaveSuccess,
  calendarDateYmd,
  devices = [],
  locations = [],
}: Props) {
  const editTarget = useSchedulingStore((s) => s.editTarget);
  const closeEditPanel = useSchedulingStore((s) => s.closeEditPanel);
  const user = useAuthStore((s) => s.user);

  const { data, isError } = useAppointmentDetails({
    appointmentId: editTarget?.appointmentId ?? '',
    patientId: editTarget?.patientId ?? '',
    providerId: editTarget?.providerId ?? '',
    appointmentProfileId: editTarget?.profileId ?? '',
    enabled: !!editTarget,
  });

  const appt = data?.appointment;

  // Don't let the pencil be a dead click. The rail renders nothing until the
  // PreLoad resolves, so a FAILED PreLoad used to look exactly like "the button
  // does nothing" — with no toast, no rail, and no way for the user to tell the
  // difference. PreLoad does fail on real rows: on dev 2026-09-09 appointment
  // f714f407-5841-4581-aaa5-6af5f2782539 returns 500 for the `billing: {}`
  // (load-invoice) variant while the `billing: null` variant returns 200, so
  // the invoice branch is what crashes. Flutter's full appointment screen sends
  // `loadInvoice: false` unless the Billing tab is open
  // (`view_appointment_screen.dart:929-931`), which is why the same appointment
  // opens there and not here. That asymmetry is a BACKEND defect and is NOT
  // worked around in the client (CLAUDE.md → "never work around backend issues
  // in the frontend"); what we fix here is the silence.
  useEffect(() => {
    if (!isError) return;
    toast.error("Couldn't open this appointment for editing. Please try again.");
    closeEditPanel();
  }, [isError, closeEditPanel]);

  // Seed the edit form ONCE per opened appointment — then freeze.
  //
  // Why a ref instead of `useMemo([appt])`: `useUpdateAppointment.onMutate`
  // OPTIMISTICALLY patches the `['appointment-details']` cache with the new
  // start/end the instant Save fires (before the PUT resolves). Re-deriving
  // `initialValues` from that patched `appt` changes `slotStart`/`selectedDate`
  // (appointment-to-form-values.ts:132-133 map both from `appt.start`), which
  // changes `CreateAppointmentPanel`'s `formKey` and REMOUNTS the form
  // mid-save. The remount unmounts the component that owns the `mutate()`
  // `onSuccess` callback, so `onClose()` never runs — the user had to press
  // Save a SECOND time to close the panel after changing the time (reported
  // reschedule bug). The form owns its edited state after mount, so re-seeding
  // from the live (optimistically-patched) cache is unwanted regardless.
  //
  // Reset when the target clears (panel closed) so the next open re-seeds from
  // a fresh load; re-seed when a different appointment id opens.
  const seedRef = useRef<{
    apptId: string;
    values: ReturnType<typeof appointmentToFormValues>;
  } | null>(null);
  if (!editTarget) {
    seedRef.current = null;
  } else if (appt && seedRef.current?.apptId !== appt.id) {
    seedRef.current = { apptId: appt.id, values: appointmentToFormValues(appt) };
  }
  const initialValues = seedRef.current?.values;

  if (!editTarget || !appt) return null;

  // PROD-2526 — a DEVICE booking opens the Book-a-device rail ITSELF, in edit
  // mode: one device panel for create, edit and reschedule, as the practitioner
  // has one appointment panel for all three (Andrew, 2026-09-23: "the UI
  // (Design) is should open the same panel of the create/edit device booking to
  // allow update"). It replaced the separate `EditDevicePanel` rail, which saved
  // through the provider route and had the old flat time list (PROD-2457).
  //
  // Still not a variant of `CreateAppointmentPanel`: that panel is built around
  // a provider from the schema up (`appointment-form.ts` hard-requires
  // `providerId`, a reminder profile and a reason code), so branching HERE, at
  // the single mount point, keeps the practitioner rail below byte-identical.
  // The practitioner seed above is skipped for this branch on purpose; the
  // device rail reads the loaded appointment directly.
  //
  // Keyed by the appointment id: the rail reads its initial state once per
  // open (no state-in-effect), so a different booking must construct a new one.
  if (isDeviceAppointment(appt)) {
    return (
      <BookDevicePanel
        key={appt.id}
        open
        edge={edge}
        layout={layout}
        editAppointment={appt}
        editIntent={editTarget.mode}
        organizationId={appt.organizationId ?? organizationId ?? user?.organizationId ?? ''}
        practiceId={appt.practiceId ?? practiceId ?? user?.practiceId ?? ''}
        userId={userId || user?.userId || ''}
        devices={devices}
        locations={locations}
        selectedDate={calendarDateYmd ? new Date(`${calendarDateYmd}T00:00:00`) : new Date()}
        onOpenChange={(next) => {
          if (!next) closeEditPanel();
        }}
        onSaved={onEditSaveSuccess}
      />
    );
  }

  return (
    <CreateAppointmentPanel
      open
      edge={edge}
      layout={layout}
      mode={editTarget.mode}
      editAppointment={appt}
      initialValues={initialValues}
      organizationId={appt.organizationId ?? organizationId ?? user?.organizationId ?? ''}
      practiceId={appt.practiceId ?? practiceId ?? user?.practiceId ?? ''}
      userId={userId || user?.userId || ''}
      onClose={closeEditPanel}
      onEditSaveSuccess={onEditSaveSuccess}
      calendarDateYmd={calendarDateYmd}
      onSearchPatients={onSearchPatients}
    />
  );
}

export default InlineEditAppointmentPanel;
